import React from 'react';

import { CoworkerGroupBanner } from './coworker-group-banner';
import { FloorplanCanvas } from './floorplan-canvas';
import { PlacementDetails } from './placement-details';
import { FloorplanMapSkeleton } from './floorplan-map-skeleton';
import { Floorplan } from '../types';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';
import { useNativeFloorplanMap } from '../hooks/use-native-floorplan-map.hook';
import { useCoworkerVisibility } from 'floorplan/hooks/use-coworker-visibility.hook';

interface FloorplanMapProps {
  floorplan: Floorplan | null;
}

export const FloorplanMap = ({ floorplan }: FloorplanMapProps): JSX.Element => {
  const isMobileDevice = useIsSmallViewportWidth();
  // Server-side stripping already keeps hidden identity out of the response; gating the drawing too
  // keeps a cached response from a previous configuration from leaking it.
  const { effectiveName } = useCoworkerVisibility();
  const {
    placements,
    dimmedResourceUuids,
    highlightedFavoriteUuids,
    favoriteResourceUuids,
    isFavoritesEnabled,
    isFavoritesLoaded,
    isLoading,
    selectedResourceUuid,
    focusedResourceUuid,
    activeGroup,
    focusGroupMemberUuids,
    selectedPlacement,
    onPlacementClick,
    closeDetails,
  } = useNativeFloorplanMap(floorplan);

  if (isLoading || !floorplan) {
    return <FloorplanMapSkeleton />;
  }

  return (
    <>
      {activeGroup && <CoworkerGroupBanner group={activeGroup} />}
      <FloorplanCanvas
        imageUrl={floorplan.imageUrl}
        imageWidth={floorplan.imageWidth}
        imageHeight={floorplan.imageHeight}
        name={floorplan.name}
        placements={placements}
        selectedResourceUuid={selectedResourceUuid}
        focusedResourceUuid={focusedResourceUuid}
        dimmedResourceUuids={dimmedResourceUuids}
        highlightedFavoriteUuids={highlightedFavoriteUuids}
        favoriteResourceUuids={favoriteResourceUuids}
        isFavoritesEnabled={isFavoritesEnabled}
        isFavoritesLoaded={isFavoritesLoaded}
        detailsPlacement={isMobileDevice ? null : selectedPlacement}
        onCloseDetails={closeDetails}
        onPlacementClick={onPlacementClick}
        showCoworkerIdentity={effectiveName}
        focusGroupMemberUuids={focusGroupMemberUuids}
      />
      {isMobileDevice && selectedPlacement && (
        <PlacementDetails
          placement={selectedPlacement}
          onClose={closeDetails}
          isFavoritesEnabled={isFavoritesEnabled}
          isFavoritesLoaded={isFavoritesLoaded}
          favoriteResourceUuids={favoriteResourceUuids}
        />
      )}
    </>
  );
};
